import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import api, { getMattersWithWip, createMatter, updateMatter, deleteMatter, getClients, getGroups, createClient,
  listMatterPermissionOverrides, addMatterPermissionOverride, removeMatterPermissionOverride } from '../services/api'
import { useAuth } from '../context/AuthContext'
import DraggablePopup from '../components/DraggablePopup'
import MatterQuickViewContent from '../components/MatterQuickViewContent'
import ClientPicker from '../components/ClientPicker'
import useFirmPracticeAreas from '../hooks/useFirmPracticeAreas'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import useTimerEntrySaved from '../hooks/useTimerEntrySaved'
import { usePendingDelete } from '../hooks/usePendingDelete'
import { useBulkUndo } from '../hooks/useBulkUndo'
import useTableSort, { SortIcon } from '../hooks/useTableSort'
import { useFormDraft } from '../hooks/useFormDraft'
import useFieldIds from '../hooks/useFieldIds'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import { removeRecentMatter, pushRecentMatter } from '../lib/recentMatters'
import { unpinMatter } from '../lib/pinnedMatters'
import { InlineSelect } from '../components/InlineEdit'
import PopOutPageButton from '../components/PopOutPageButton'
import { SkeletonTable, LoadingRegion } from '../components/Skeleton'
import EmptyState from '../components/EmptyState'
import ContextMenu from '../components/ContextMenu'
import { safeOpenPopupWindow } from '../services/token'
import HelpButton from '../components/HelpButton'
import MatterBlockListSection from '../components/MatterBlockListSection'
import CustomFieldsSection from '../components/CustomFieldsSection'
import { displayClientForMatterRow } from '../lib/displayClient'
import { reopenConfirmOptions } from '../lib/matterReopen'
import { bulkCloseNoteOptions } from '../lib/matterClose'

export default function Matters() {
  const { user } = useAuth()
  const pending = usePendingDelete()
  const sort = useTableSort('matters', { key: 'title', dir: 'asc' })
  const toast = useToast()
  const ask = useAsk()
  const undo = useBulkUndo()
  // Feedback #510: firm-configurable practice areas via /api/practice-areas.
  const { areas: practiceAreas } = useFirmPracticeAreas()
  const [searchParams, setSearchParams] = useSearchParams()
  const [matters, setMatters] = useState([])
  const [clients, setClients] = useState([])
  // Feedback #475 (Shawn 2026-08-04): inline "+ New client" mini-form on
  // the matter-create page.  Avoids the "open Clients page → create client →
  // navigate back → find client in dropdown" round-trip.  On save, the new
  // client is added to the local `clients` array + auto-selected.
  const [showInlineClientForm, setShowInlineClientForm] = useState(false)
  const [inlineClientBusy, setInlineClientBusy] = useState(false)
  const [inlineClientError, setInlineClientError] = useState('')
  const [inlineClientForm, setInlineClientForm] = useState({
    client_type: 'individual',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  })
  const [teamMembers, setTeamMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingMatter, setEditingMatter] = useState(null)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('active')
  const [quickView, setQuickView] = useState(null)
  const [quickViewData, setQuickViewData] = useState(null)
  const [rowCtxMenu, setRowCtxMenu] = useState(null)
  // Bulk-select state — Set of selected matter ids for the bulk-status action bar
  const [selectedMatterIds, setSelectedMatterIds] = useState(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const navigate = useNavigate()

  // Native DOM contextmenu listener — sidesteps React synthetic events which
  // don't bubble up reliably for <tr> targets in this layout (Steve 2026-06-29).
  // We tag each row with data-matter-id and catch contextmenu at document level
  // via closest() lookup. The accompanying React onContextMenu on the <tr> is
  // also still wired for browser environments without the document listener.
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest('tr[data-matter-id]')
      if (!tr) return
      const matterId = parseInt(tr.dataset.matterId, 10)
      const matter = matters.find(m => m.id === matterId)
      if (!matter) return
      e.preventDefault()
      const items = [
        { label: 'Open',                icon: '↗', onClick: () => navigate(`/matters/${matter.id}`) },
        { label: 'Open quick view',     icon: '👁', onClick: () => { setQuickView(matter); setQuickViewData(null); api.get(`/api/matters/${matter.id}`).then(res => setQuickViewData(res.data)).catch(() => {}) } },
      ]
      if (window.electronAPI?.openPopupWindow) {
        items.push({ label: 'Open in new window', icon: '⤴', onClick: () => {
          safeOpenPopupWindow(
            `page-/matters/${matter.id}`,
            `/popup/page/matters/${matter.id}`,
            { width: 1300, height: 900 }
          )
        }})
      }
      setRowCtxMenu({ x: e.clientX, y: e.clientY, items })
    }
    document.addEventListener('contextmenu', handler)
    return () => document.removeEventListener('contextmenu', handler)
  }, [matters, navigate])

  usePageShortcut('n', () => { setEditingMatter(null); setShowForm(true) })
  usePageShortcut('f', () => document.querySelector('input[placeholder*="Search matters"]')?.focus())

  const [groups, setGroups] = useState([])
  // Firm-wide default for new matters (set on Firm Settings page); falls back
  // to 'everyone' / [] until loaded so the form is never blocked on the fetch.
  const [firmDefaultVis, setFirmDefaultVis] = useState({ visibility: 'everyone', visible_to_groups: [] })
  // Matter templates for the apply-on-create picker.  Lazy — fetched once
  // when the create form is opened.  applyTemplateId is the picker value;
  // when set, the picked template's defaults pre-fill the form + on save
  // we POST /templates/:id/apply/:matterId to auto-create tasks + SOL.
  const [templates, setTemplates] = useState([])
  const [applyTemplateId, setApplyTemplateId] = useState('')
  const blankForm = {
    client_id: '', title: '', description: '', status: 'active',
    billing_type: 'hourly', rate: '', retainer: '',
    file_number: '', practice_area: '', responsible_attorney: '',
    ledes_client_id: '', ledes_law_firm_matter_id: '', ledes_client_matter_id: '',
    // T2 Ship A polish FE (2026-08-11): flat_fee_amount capture (only meaningful
    // when billing_type='flat_fee'; UI shows the field only in that mode).
    flat_fee_amount: '',
    // T2 Ship I rest (2026-08-11): per-matter bill-to override.
    bill_to_company: '', bill_to_street: '', bill_to_city: '', bill_to_state: '', bill_to_zip: '',
    // Ship 7g (2026-08-24): per-matter invoice template override.
    // '' = defer to firm default; enum value = per-matter override.
    invoice_template_override: '',
    visibility: firmDefaultVis.visibility,
    visible_to_users: [],
    visible_to_groups: firmDefaultVis.visibility === 'specific' ? [...firmDefaultVis.visible_to_groups] : []
  }
  const [form, setForm] = useState(blankForm)
  // T2 Ship I rest (2026-08-11) — track whether the collapsible bill-to
  // section has been opened+edited THIS session.  Pre-code panel CRITICAL
  // (Sol + Grok convergent): if we always include bill_to_* in the PUT body,
  // a closed <details> sending '' would touched-clear the values whenever
  // user edits unrelated matter fields.  Backend semantic: absent =
  // preserve, '' = clear.  Frontend sends bill_to_* ONLY when this flag is
  // set true (user has intentionally opened + interacted with the section).
  const [billToDirty, setBillToDirty] = useState(false)
  // T2 Ship A polish FE — same pattern for flat_fee_amount (only include
  // in PUT when user has touched it).
  const [flatFeeDirty, setFlatFeeDirty] = useState(false)
  // Ship 7g — same touched-flag pattern for invoice_template_override.
  // Absent = preserve; '' = clear (defer to firm default); enum = set.
  const [invoiceTemplateDirty, setInvoiceTemplateDirty] = useState(false)
  // Track 2 follow-up (2026-10-19) — the status the edit form LOADED.  A
  // staff status change locks the matter's status against the Amicus import
  // (PUT sets status_locked when the sent status differs from the stored one),
  // so the form sends `status` only when the user changed it: resending the
  // loaded value from a stale page would write an old status back AND lock it.
  const [editingOriginalStatus, setEditingOriginalStatus] = useState(null)
  // The form as the edit LOADED it, so "Close Matter…" can tell whether
  // leaving for the matter page would drop unsaved edits (it asks first).
  const [editingLoadedForm, setEditingLoadedForm] = useState(null)
  // Firm default surfaced next to the "(Firm default)" picker option so
  // admins see WHICH template inheriting resolves to.  Populated by the
  // Settings fetch in fetchData.
  const [firmDefaultTemplate, setFirmDefaultTemplate] = useState('standard')
  // Field ids for WCAG 2.1 label association (htmlFor ↔ id).  Retrofit
  // added 2026-07-16 so screen readers announce label on input focus.
  const fid = useFieldIds()

  // Auto-save the new-matter form as a draft to localStorage so a crash or
  // accidental tab close doesn't lose 20 minutes of typing.  Only runs on the
  // CREATE path — editing a matter already persists the row on save, and a
  // stale edit-draft would be more confusing than helpful.
  const matterDraft = useFormDraft('lextrack_draft_matter_new', form, setForm, {
    enabled: showForm && !editingMatter,
    initialForm: blankForm,
    debounceMs: 3000,
  })

  useEffect(() => { fetchData() }, [])
  useTimerEntrySaved(() => fetchData())

  // Lazy-load matter templates the first time the create form opens.
  useEffect(() => {
    if (!showForm || editingMatter || templates.length > 0) return
    api.get('/api/matter-templates')
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]))
  }, [showForm, editingMatter, templates.length])

  // When the user picks a template, pre-fill matching form fields (only
  // ones the template defines; leave user-set fields alone).
  const applyTemplatePrefill = (tid) => {
    setApplyTemplateId(tid)
    if (!tid) return
    const t = templates.find(x => String(x.id) === String(tid))
    if (!t) return
    setForm(f => ({
      ...f,
      practice_area:  t.practice_area || f.practice_area,
      billing_type:   t.default_billing_type || f.billing_type,
      rate:           t.default_rate != null ? String(t.default_rate) : f.rate,
      retainer:       t.default_retainer != null ? String(t.default_retainer) : f.retainer,
      // budget fields — MatterDetail's BudgetCard exposes these too
      budget_amount:  t.default_budget_amount != null ? String(t.default_budget_amount) : (f.budget_amount || ''),
      budget_type:    t.default_budget_type || (f.budget_type || ''),
    }))
  }

  // Command palette (Ctrl+K → "New matter") lands us here with ?new=1;
  // auto-open the create form and clean the query param.
  // Feedback #508: also accepts ?client=<id> from ClientDetail's "+ New Matter"
  // button so the client picker is pre-populated.  The form-draft rehydration
  // runs BEFORE this effect on mount, so we set client_id AFTER the draft
  // decision, and only when client is provided.
  useEffect(() => {
    const isNew = searchParams.get('new') === '1'
    const clientFromQs = searchParams.get('client')
    if (isNew) {
      setShowForm(true)
      if (clientFromQs) {
        setForm(f => ({ ...f, client_id: clientFromQs }))
      }
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const fetchData = async () => {
    try {
      const [mattersRes, clientsRes, teamRes] = await Promise.all([
        getMattersWithWip(), getClients(), api.get('/api/team')
      ])
      setMatters(mattersRes.data)
      setClients(clientsRes.data)
      setTeamMembers(teamRes.data.filter(m => m.is_active))
      // Groups load is best-effort — staff without admin/can_delete_clients still
      // get the matters page, but they can't read /api/team/groups (admin-only-ish).
      try { const g = await getGroups(); setGroups(g.data) } catch { setGroups([]) }
      // Pull firm-wide new-matter visibility defaults so the create form pre-fills
      try {
        const s = await api.get('/api/settings')
        setFirmDefaultVis({
          visibility: s.data?.default_matter_visibility || 'everyone',
          visible_to_groups: s.data?.default_matter_visible_groups || []
        })
        // Ship 7g — capture firm default invoice template so the picker's
        // "Firm default" option can show which template inheriting resolves to.
        setFirmDefaultTemplate(s.data?.default_invoice_template || 'standard')
      } catch { /* fine — keep the 'everyone' fallback */ }
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading matters')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (matter) => {
    setEditingMatter(matter.id)
    const loaded = {
      client_id: matter.client_id || '',
      title: matter.title || '',
      description: matter.description || '',
      status: matter.status || 'active',
      billing_type: matter.billing_type || 'hourly',
      rate: matter.rate || '',
      retainer: matter.retainer || '',
      retainer_target: matter.retainer_target || '',
      retainer_reminder_threshold_pct: matter.retainer_reminder_threshold_pct || 20,
      trust_auto_apply_enabled: matter.trust_auto_apply_enabled !== false,
      shares_client_trust_pool: matter.shares_client_trust_pool === true,
      file_number: matter.file_number || '',
      practice_area: matter.practice_area || '',
      responsible_attorney: matter.responsible_attorney || '',
      ledes_client_id: matter.ledes_client_id || '',
      ledes_law_firm_matter_id: matter.ledes_law_firm_matter_id || '',
      ledes_client_matter_id: matter.ledes_client_matter_id || '',
      // T2 Ship A polish FE + Ship I rest — hydrate from existing matter row.
      flat_fee_amount: matter.flat_fee_amount != null ? String(matter.flat_fee_amount) : '',
      bill_to_company: matter.bill_to_company || '',
      bill_to_street:  matter.bill_to_street  || '',
      bill_to_city:    matter.bill_to_city    || '',
      bill_to_state:   matter.bill_to_state   || '',
      bill_to_zip:     matter.bill_to_zip     || '',
      // Ship 7g — hydrate template override from row; '' = firm-default.
      invoice_template_override: matter.invoice_template_override || '',
      visibility: matter.visibility || 'everyone',
      visible_to_users: matter.visible_to_users || [],
      visible_to_groups: matter.visible_to_groups || []
    }
    setForm(loaded)
    setEditingLoadedForm(loaded)
    setBillToDirty(false)   // Reset dirty state on edit-mount
    setFlatFeeDirty(false)
    setInvoiceTemplateDirty(false)  // Ship 7g — reset template dirty on edit-mount
    setEditingOriginalStatus(matter.status || 'active')
    setShowForm(true)
    window.scrollTo(0, 0)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.client_id) {
      setError('Client is required. Pick a client from the dropdown — every matter must be assigned to a client.')
      return
    }
    if (!form.title?.trim()) {
      setError('Matter title is required.')
      return
    }
    // Warn non-admin users if they're about to lock themselves OUT of a matter
    // they're editing by setting visibility='specific' without including
    // themselves (and not being the responsible_attorney). Admins always see
    // everything, so the warning doesn't apply to them.
    const isAdminUser = user?.role === 'admin' || user?.permissions?.is_admin === true
    if (editingMatter && !isAdminUser && form.visibility === 'specific') {
      const userInList = form.visible_to_users.map(Number).includes(Number(user.id))
      const isResponsible = Number(form.responsible_attorney) === Number(user.id)
      if (!userInList && !isResponsible) {
        const ok = await ask({
          title: 'Lock yourself out?',
          message: 'Warning: with these visibility settings you will LOSE access to this matter after saving (you are not in the visible-users list, not in any of the listed groups, and not the responsible attorney). Are you sure?',
          confirmLabel: 'Save anyway',
          danger: true,
        })
        if (!ok) return
      }
    }
    // A reopen (closed → active or pending) clears the closing record: ask
    // first, in the same words as Reopen Matter (round-3 review, 2026-10-20).
    if (editingMatter && editingOriginalStatus === 'closed' && form.status !== 'closed') {
      if (!(await ask(reopenConfirmOptions(form.status)))) return
    }
    try {
      // T2 Ship I rest + Ship A polish FE — build submit body respecting
      // section-dirty flags.  Pre-code panel CRITICAL: touched-flag pattern
      // treats field ABSENCE as preserve.  Only include bill_to_* fields
      // when billToDirty === true; only include flat_fee_amount when
      // flatFeeDirty === true.  Prevents unrelated matter edits from
      // wiping bill-to overrides or flat-fee values.
      const buildSubmitBody = () => {
        const body = { ...form }
        if (!billToDirty) {
          delete body.bill_to_company
          delete body.bill_to_street
          delete body.bill_to_city
          delete body.bill_to_state
          delete body.bill_to_zip
        }
        if (!flatFeeDirty) {
          delete body.flat_fee_amount
        }
        // Ship 7g — same touched-flag semantic for invoice_template_override.
        // Absent = preserve.  Present '' = clear (defer to firm default).
        // Present enum value = per-matter override.
        if (!invoiceTemplateDirty) {
          delete body.invoice_template_override
        }
        // Edit only: an unchanged status is absent (= preserve), never resent.
        if (editingMatter && body.status === editingOriginalStatus) {
          delete body.status
        }
        // Create: the form offers only Active and Pending (POST /api/matters
        // refuses 'closed'); a value it can't show (an old saved draft) is sent
        // as the Active the select displays.
        if (!editingMatter && body.status !== 'active' && body.status !== 'pending') {
          body.status = 'active'
        }
        // A status change carries the status the form loaded: if the matter
        // was closed or reopened since, the API refuses (409
        // matter_status_changed) instead of acting on a stale form.
        if (editingMatter && body.status !== undefined) {
          body.expected_status = editingOriginalStatus
        }
        return body
      }

      if (editingMatter) {
        await updateMatter(editingMatter, buildSubmitBody())
        setEditingMatter(null)
      } else {
        const created = await createMatter(buildSubmitBody())
        matterDraft.clear()  // successful create — wipe the persisted draft
        // Apply template's tasks + SOL if one was picked.  Fire-and-forget
        // so a slow template apply doesn't block the "matter created" toast.
        if (applyTemplateId && created?.data?.id) {
          try {
            const res = await api.post(`/api/matter-templates/${applyTemplateId}/apply/${created.data.id}`)
            if (res.data?.tasks_created > 0 || res.data?.sol_created) {
              const parts = []
              if (res.data.tasks_created) parts.push(`${res.data.tasks_created} task${res.data.tasks_created === 1 ? '' : 's'}`)
              if (res.data.sol_created) parts.push('SOL entry')
              setError('')
              // Success is a nudge, not an error, but no toast in scope here — piggyback on setError with success styling not possible.  Silent success is acceptable since the tasks/SOL are visible on the matter page.
            }
          } catch (err) { /* apply failure shouldn't undo the create */ }
        }
      }
      setForm(blankForm)
      setApplyTemplateId('')
      setShowForm(false)
      setError('')
      fetchData()
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving matter')
    }
  }

  // "Send them to Close/Reopen" (James 2026-10-19).  The edit form does not
  // close a matter — PUT /api/matters/:id answers 400 'use_close_matter' — so
  // an open matter's form offers no Closed status and this action instead:
  // it opens the matter page with ?close=1, where MatterDetail opens Close
  // Matter (closing notes, pre-close checklist, trust guard).  Shown only to
  // those who could click Close Matter there (admin or can_create_matters,
  // as POST /:id/close).  Unsaved edits on this form are lost by leaving, so
  // it asks first when there are any.
  const canCloseMatters = user?.role === 'admin' || user?.permissions?.is_admin === true ||
    user?.permissions?.can_create_matters === true
  const goToCloseMatter = async () => {
    const matterId = editingMatter
    if (!matterId) return
    const dirty = !editingLoadedForm || JSON.stringify(form) !== JSON.stringify(editingLoadedForm)
    if (dirty) {
      const ok = await ask({
        type: 'confirm',
        title: 'Leave without saving?',
        message: 'Close Matter opens on the matter page. The changes you have not saved on this form will be lost.\n\nTo keep them, Cancel, click Save Changes, then use Close Matter on the matter page.',
        confirmLabel: 'Go to Close Matter',
        danger: true,
      })
      if (!ok) return
    }
    setShowForm(false)
    setEditingMatter(null)
    setEditingLoadedForm(null)
    setForm(blankForm)
    setError('')
    navigate(`/matters/${matterId}?close=1`)
  }

  const toggleVisUser = (uid) => {
    setForm(f => ({ ...f, visible_to_users: f.visible_to_users.includes(uid)
      ? f.visible_to_users.filter(x => x !== uid) : [...f.visible_to_users, uid] }))
  }
  const toggleVisGroup = (gid) => {
    setForm(f => ({ ...f, visible_to_groups: f.visible_to_groups.includes(gid)
      ? f.visible_to_groups.filter(x => x !== gid) : [...f.visible_to_groups, gid] }))
  }

  const handleDelete = async (id) => {
    // Matters cascade — keep the blocking confirm() AND layer on the 5-sec
    // Undo toast as a second safety net.  During the 5-sec window NOTHING
    // has been touched server-side yet, so Undo fully restores.
    const row = matters.find((m) => m.id === id)
    const ok = await ask({
      title: 'Delete matter?',
      message: `Delete matter "${row?.title || '(untitled)'}"?\n\nAll time entries, invoices, notes, events, and communications on this matter will also be permanently deleted.  This cannot be undone once the 5-second window closes.`,
      confirmLabel: 'Delete',
      danger: true,
    })
    if (!ok) return
    pending.schedule({
      url: `/api/matters/${id}`,
      message: `Matter deleted — ${row?.title || '(untitled)'}`,
      toneAsWarning: true,
      onOptimisticRemove: () => {
        setMatters((prev) => prev.filter((m) => m.id !== id))
        removeRecentMatter(id)   // drop from sidebar Recent list too
        unpinMatter(id)          // drop from Pinned list — no stale pins to deleted matters
      },
      onRestore: () => {
        fetchData()
        // Re-add to Recent so muscle-memory still works after an undo
        if (row) pushRecentMatter({ id: row.id, title: row.title })
      },
      onDeleteError: (err) => {
        setError(err.response?.data?.error || err.message || 'Error deleting matter')
        fetchData()
      }
    })
  }

  // Compute the visible + sorted matter list once at component-scope so the
  // header checkbox + bulk-action bar can reference it without duplicating
  // the filter/sort logic.  Rendered inside <tbody> too.
  const visibleMatters = (() => {
    let f = [...matters].filter(m => {
      if (filter === 'active' && m.status !== 'active') return false
      if (filter === 'closed' && m.status !== 'closed') return false
      if (!search) return true
      const q = search.toLowerCase()
      return m.title?.toLowerCase().includes(q) ||
        `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase().includes(q) ||
        (m.company_name || '').toLowerCase().includes(q) ||
        m.responsible_attorney_name?.toLowerCase().includes(q) ||
        m.file_number?.toLowerCase().includes(q)
    })
    return sort.apply(f, {
      title: (m) => (m.title || '').toLowerCase(),
      client: (m) => `${m.last_name || ''} ${m.first_name || ''}`.toLowerCase(),
      attorney: (m) => (m.responsible_attorney_name || '').toLowerCase(),
      file_number: (m) => (m.file_number || '').toLowerCase(),
      status: (m) => m.status || '',
      wip: (m) => parseFloat(m.wip_amount || 0),
    }) || f
  })()

  // ── Bulk selection + bulk-status change ────────────────────────────
  const toggleMatter = (id) => {
    setSelectedMatterIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const toggleAllVisible = (rows, on) => {
    setSelectedMatterIds((prev) => {
      const next = new Set(prev)
      rows.forEach((r) => { if (on) next.add(r.id); else next.delete(r.id) })
      return next
    })
  }
  const clearMatterSelection = () => setSelectedMatterIds(new Set())

  // Undo of a bulk status change (James 2026-10-19, "Undo restores the old
  // lock").  It used to re-send the old status per bucket, which the server
  // treats as a fresh staff change and LOCKS against the Amicus import.  Now
  // the bulk call answers with an undo_token — signed by the server, holding
  // each changed matter's prior status AND status_locked — and the Undo sends
  // only that back, so the server restores both exactly as it saw them.  A
  // matter edited since the bulk change is left alone (reported as skipped).
  const revertBulkStatus = (undoToken) => async () => {
    if (!undoToken) throw new Error('nothing changed, so there is nothing to undo')
    const res = await api.put('/api/matters/bulk/status', { undo_token: undoToken })
    const left = res.data?.skipped?.length || 0
    if (left > 0) {
      toast.warn(`${left} matter${left === 1 ? ' was' : 's were'} changed after the bulk change and left as ${left === 1 ? 'it is' : 'they are'}.`)
    }
  }

  const handleBulkStatus = async (status) => {
    const ids = Array.from(selectedMatterIds)
    if (ids.length === 0) return
    // For CLOSE (James 2026-10-20, "Require one closing note"): ask for ONE
    // closing note — it also warns about the trust-zero guard.  The API saves
    // it as the closing notes of every matter it closes and refuses a close
    // without one.  Cancel or a blank note closes nothing.
    let body = { ids, status }
    if (status === 'closed') {
      const typed = await ask(bulkCloseNoteOptions(ids.length))
      const closingNote = typeof typed === 'string' ? typed.trim() : ''
      if (!closingNote) return
      body = { ids, status, closing_notes: closingNote }
    }
    // No local snapshot: the Undo restores from the server's undo_token
    // (see revertBulkStatus), which also carries each matter's prior lock;
    // the server puts back the old closing notes too.
    setBulkBusy(true)
    try {
      let res
      try {
        res = await api.put('/api/matters/bulk/status', body)
      } catch (err) {
        // If trust-balance guard rejected, offer override path
        const data = err.response?.data
        if (data?.matters_with_trust && status === 'closed') {
          const titles = data.matters_with_trust.map(m => `  • ${m.title} — $${parseFloat(m.balance).toFixed(2)}`).join('\n')
          const reason = await ask({
            title: 'Trust balance guard',
            message: `${data.matters_with_trust.length} matter(s) have non-zero trust balances:\n\n${titles}\n\n` +
            `Type a reason to OVERRIDE the guard + close them anyway, or Cancel to abort.`,
            type: 'textarea',
            required: true,
            danger: true,
            confirmLabel: 'Close anyway',
          })
          if (reason && reason.trim()) {
            try {
              // The same closing note again, now with the override reason.
              const res2 = await api.put('/api/matters/bulk/status', {
                ...body, override_trust_balance: true, reason: reason.trim(),
              })
              clearMatterSelection()
              // Fire undo toast for the override-close path too.
              undo.run({
                ids,
                apply: async () => {},
                revert: revertBulkStatus(res2.data.undo_token),
                label: `Closed ${res2.data.updated} matter(s) with override. Reason recorded in audit log.`,
                onAfter: () => fetchData(),
              })
            } catch (err2) {
              toast.error(err2.response?.data?.error || 'Bulk close override failed')
            }
          }
        } else {
          toast.error(data?.error || 'Bulk status change failed')
        }
        return
      }
      clearMatterSelection()
      undo.run({
        ids,
        apply: async () => {}, // already fired above
        revert: revertBulkStatus(res.data.undo_token),
        label: `Updated ${res.data.updated} of ${res.data.requested} matter${res.data.requested === 1 ? '' : 's'} to ${status}.`,
        onAfter: () => fetchData(),
      })
    } finally { setBulkBusy(false) }
  }

  const statusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700'
      case 'closed': return 'bg-gray-100 text-gray-700'
      case 'pending': return 'bg-yellow-100 text-yellow-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }
  const STATUS_OPTIONS = [
    { value: 'active',  label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'closed',  label: 'Closed' }
  ]
  const canEditMatters = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_edit_matters

  // Inline field save (the status badge).  Sends ONLY the changed field: PUT
  // /api/matters/:id preserves every column it is not sent (partial-PUT
  // preservation, 2026-09-15), and resending the whole list row could write
  // stale values back.  A status change also sends the status this row was
  // loaded with (expected_status): if someone closed or reopened the matter
  // since the list loaded, the API refuses with a 409 instead of reopening it
  // from a stale row (round-3 review, 2026-10-20), and the list reloads.  A
  // reopen (closed → active/pending) clears the closing record, so it asks
  // first, as Reopen Matter does.  On success the API's row is merged back so
  // derived fields (updated_at, joined names, aggregates) stay accurate.
  const saveInlineField = async (row, field, value) => {
    if (field === 'status' && row.status === 'closed' && value !== 'closed') {
      if (!(await ask(reopenConfirmOptions(value)))) return
    }
    try {
      const body = { [field]: value }
      if (field === 'status') body.expected_status = row.status
      const res = await updateMatter(row.id, body)
      setMatters((prev) => prev.map((m) => m.id === row.id ? { ...m, ...res.data } : m))
      toast.success(`Matter updated`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save — reverted')
      if (err.response?.data?.code === 'matter_status_changed') fetchData()
      throw err   // let InlineEdit revert its draft
    }
  }

  const openQuickView = async (matter) => {
    setQuickView(matter)
    setQuickViewData(null)
    try {
      const res = await api.get(`/api/matters/${matter.id}`)
      setQuickViewData(res.data)
    } catch (err) { /* popup shows basic info from list */ }
  }

  const handleMatterClick = (e, matter) => {
    // Single click → open matter detail in a NEW Electron window (Steve preference
    // 2026-06-29).  In Electron the safeOpenPopupWindow helper migrates auth token
    // if needed, spawns a top-level BrowserWindow.  In a browser this falls back
    // to navigating within the current tab (no equivalent of "new OS window" in a
    // pure web context).  Right-click context menu still has "Open" (same-window)
    // + "Open quick view" if needed.
    if (window.electronAPI?.openPopupWindow) {
      safeOpenPopupWindow(
        `page-/matters/${matter.id}`,
        `/popup/page/matters/${matter.id}`,
        { width: 1300, height: 900 }
      )
    } else {
      navigate(`/matters/${matter.id}`)
    }
  }

  if (loading) return (
    <LoadingRegion label="Loading matters">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="h-8 w-40 bg-gray-100 rounded animate-pulse mb-4" />
        <SkeletonTable rows={8} cols={6} />
      </div>
    </LoadingRegion>
  )

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-gray-800">Matters<HelpButton slug="matters" /></h2>
          <PopOutPageButton title="Matters" width={1300} height={900} />
        </div>
        <button
          onClick={() => {
            // When OPENING the new-matter form, re-seed from current firmDefaultVis
            // (which may have loaded after the initial render's blankForm capture).
            if (!showForm) {
              setEditingMatter(null)
              setForm({
                client_id: '', title: '', description: '', status: 'active',
                billing_type: 'hourly', rate: '', retainer: '',
                file_number: '', practice_area: '', responsible_attorney: '',
    ledes_client_id: '', ledes_law_firm_matter_id: '', ledes_client_matter_id: '',
                visibility: firmDefaultVis.visibility,
                visible_to_users: [],
                visible_to_groups: firmDefaultVis.visibility === 'specific' ? [...firmDefaultVis.visible_to_groups] : []
              })
            }
            setShowForm(!showForm)
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + New Matter
        </button>
      </div>

      {/* New Matter Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">New Matter</h3>
          {matterDraft.banner}
          {/* Matters depth #2 UI — template picker.  Only shown on CREATE
              (not edit), only when templates exist.  Selecting a template
              pre-fills matching fields + queues an apply-tasks-and-SOL
              call after the matter is created. */}
          {!editingMatter && templates.length > 0 && (
            <div className="mb-4 bg-primary-50/60 border border-primary-100 rounded-lg p-3 flex items-center gap-3 flex-wrap text-sm">
              <span className="text-primary-800">📁</span>
              <label className="flex items-center gap-2">
                <span className="text-primary-900 font-medium">Start from template:</span>
                <select value={applyTemplateId} onChange={e => applyTemplatePrefill(e.target.value)}
                  className="border border-primary-300 rounded px-2 py-1 text-sm bg-white">
                  <option value="">— none (blank matter) —</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.practice_area ? ` — ${t.practice_area}` : ''}
                      {t.task_count ? ` (${t.task_count} tasks)` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {applyTemplateId && (
                <span className="text-xs text-primary-700">
                  Defaults pre-filled below · tasks + SOL will be created after save.
                </span>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Feedback #475: client picker + inline "+ New client" button */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label htmlFor={fid('client')} className="block text-sm font-medium text-gray-700">Client</label>
                {!editingMatter && (
                  <button type="button"
                    onClick={() => {
                      setShowInlineClientForm(v => !v)
                      setInlineClientError('')
                    }}
                    className="text-xs text-primary-600 hover:text-primary-800 hover:underline"
                    title="Create a new client without leaving this form"
                  >
                    {showInlineClientForm ? '× Cancel new client' : '+ New client'}
                  </button>
                )}
              </div>
              {/* Feedback #505: type-ahead searchable client picker + alphabetical sort.
                  See ClientPicker component for display-name rules (companies + individuals
                  sort together instead of clumping under empty last_name). */}
              <ClientPicker
                id={fid('client')}
                required
                clients={clients}
                value={form.client_id}
                onChange={(newId) => setForm({...form, client_id: newId})}
                placeholder="Start typing a client name…"
              />
              {/* Inline "+ New client" compact form */}
              {showInlineClientForm && (
                <div className="mt-3 p-4 bg-primary-50 border border-primary-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-xs text-primary-700 font-medium">
                    ✨ New client
                    <span className="text-primary-500 font-normal">(saves + auto-selects when you click Add)</span>
                  </div>
                  <select
                    value={inlineClientForm.client_type}
                    onChange={e => setInlineClientForm({...inlineClientForm, client_type: e.target.value})}
                    className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="individual">Individual (a person)</option>
                    <option value="company">Company (a business entity)</option>
                  </select>
                  {inlineClientForm.client_type === 'company' && (
                    <input type="text"
                      value={inlineClientForm.company_name}
                      onChange={e => setInlineClientForm({...inlineClientForm, company_name: e.target.value})}
                      placeholder="Company Name *"
                      className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text"
                      required
                      value={inlineClientForm.first_name}
                      onChange={e => setInlineClientForm({...inlineClientForm, first_name: e.target.value})}
                      placeholder={inlineClientForm.client_type === 'company' ? 'Contact — First *' : 'First name *'}
                      className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <input type="text"
                      required
                      value={inlineClientForm.last_name}
                      onChange={e => setInlineClientForm({...inlineClientForm, last_name: e.target.value})}
                      placeholder={inlineClientForm.client_type === 'company' ? 'Contact — Last *' : 'Last name *'}
                      className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="email"
                      value={inlineClientForm.email}
                      onChange={e => setInlineClientForm({...inlineClientForm, email: e.target.value})}
                      placeholder="Email"
                      className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                    <input type="text"
                      value={inlineClientForm.phone}
                      onChange={e => setInlineClientForm({...inlineClientForm, phone: e.target.value})}
                      placeholder="Phone"
                      className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm bg-white"
                    />
                  </div>
                  {inlineClientError && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{inlineClientError}</p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button type="button"
                      disabled={inlineClientBusy}
                      onClick={() => { setShowInlineClientForm(false); setInlineClientError('') }}
                      className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button type="button"
                      disabled={inlineClientBusy}
                      onClick={async () => {
                        setInlineClientError('')
                        if (inlineClientForm.client_type === 'company' && !inlineClientForm.company_name.trim()) {
                          setInlineClientError('Company Name is required.')
                          return
                        }
                        if (!inlineClientForm.first_name.trim() || !inlineClientForm.last_name.trim()) {
                          setInlineClientError('First + Last name are required.')
                          return
                        }
                        setInlineClientBusy(true)
                        try {
                          // POST /api/clients via the same helper Clients.js uses.
                          const res = await createClient(inlineClientForm)
                          const newClient = res.data
                          // Add to local clients list + auto-select
                          setClients(prev => [...prev, newClient])
                          setForm(f => ({...f, client_id: String(newClient.id)}))
                          // Reset the inline form + collapse
                          setInlineClientForm({
                            client_type: 'individual',
                            company_name: '',
                            first_name: '',
                            last_name: '',
                            email: '',
                            phone: '',
                          })
                          setShowInlineClientForm(false)
                        } catch (err) {
                          setInlineClientError(err.response?.data?.error || 'Could not create client')
                        } finally {
                          setInlineClientBusy(false)
                        }
                      }}
                      className="text-xs px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {inlineClientBusy ? 'Adding…' : '+ Add & select'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label htmlFor={fid('matter-title')} className="block text-sm font-medium text-gray-700 mb-1">Matter Title</label>
              <input id={fid('matter-title')}
                type="text"
                required
                value={form.title}
                onChange={e => setForm({...form, title: e.target.value})}
                placeholder="e.g. Doe v. Smith"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('billing-type')} className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
              <select id={fid('billing-type')}
                value={form.billing_type}
                onChange={e => setForm({...form, billing_type: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="hourly">Hourly</option>
                <option value="flat_fee">Flat Fee</option>
                <option value="retainer">Retainer</option>
                <option value="contingency">Contingency</option>
              </select>
            </div>
            <div>
              <label htmlFor={fid('hourly-rate')} className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
              <input id={fid('hourly-rate')}
                type="number"
                value={form.rate}
                onChange={e => setForm({...form, rate: e.target.value})}
                placeholder="250.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('retainer')} className="block text-sm font-medium text-gray-700 mb-1">Retainer ($)</label>
              <input id={fid('retainer')}
                type="number"
                value={form.retainer}
                onChange={e => setForm({...form, retainer: e.target.value})}
                placeholder="5000.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {/* Batch D-D.5: retainer automation fields */}
            <div>
              <label htmlFor={fid('retainer-target')} className="block text-sm font-medium text-gray-700 mb-1">
                Retainer target ($) <span className="text-xs text-gray-400 font-normal">(auto-reminders)</span>
              </label>
              <input id={fid('retainer-target')}
                type="number"
                value={form.retainer_target || ''}
                onChange={e => setForm({...form, retainer_target: e.target.value})}
                placeholder="5000.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Nightly cron emails the client when trust balance drops below <strong>{form.retainer_reminder_threshold_pct || 20}%</strong> of this target.  Blank = no reminders.
              </p>
            </div>
            <div>
              <label htmlFor={fid('retainer-threshold')} className="block text-sm font-medium text-gray-700 mb-1">
                Reminder threshold (%)
              </label>
              <input id={fid('retainer-threshold')}
                type="number" min="0" max="100"
                value={form.retainer_reminder_threshold_pct || ''}
                onChange={e => setForm({...form, retainer_reminder_threshold_pct: e.target.value})}
                placeholder="20"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="col-span-full">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={form.trust_auto_apply_enabled !== false}
                  onChange={e => setForm({...form, trust_auto_apply_enabled: e.target.checked})}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  Auto-apply trust to invoices on this matter
                  <span className="block text-xs text-gray-500 mt-0.5">
                    When the firm's <strong>trust-first invoicing</strong> is enabled (Settings → Invoicing), each new earned-fee invoice on this matter auto-applies trust funds up to the invoice total.  Uncheck to keep manual "Pay from trust" flow on this matter only.
                  </span>
                </span>
              </label>
            </div>
            {/* Feedback #509 — shared retainer trust pool.  When enabled, this
                matter's retainer-target checks + reminder emails treat all of
                the client's other pool-enabled matters as a single pooled
                balance.  Rule 1.15-clean because client ledgers stay separate
                per-client. */}
            <div className="col-span-full">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={form.shares_client_trust_pool === true}
                  onChange={e => setForm({...form, shares_client_trust_pool: e.target.checked})}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  Share retainer trust with other matters for this client
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Enable when this client has multiple matters that draw from the same trust deposit.  Retainer-target checks + low-balance reminders will pool balances across every matter you've flagged this way for this client.  <strong>Client ledgers stay separate per-client</strong>, so pooling is legally clean under Cal. Rule 1.15.
                  </span>
                </span>
              </label>
            </div>
            <div>
              <label htmlFor={fid('file-number')} className="block text-sm font-medium text-gray-700 mb-1">
                File Number <span className="text-xs text-gray-400 font-normal">(auto-generated if blank)</span>
              </label>
              <input id={fid('file-number')}
                type="text"
                value={form.file_number || ''}
                onChange={e => setForm({...form, file_number: e.target.value})}
                placeholder="Leave blank for Amicus-style (e.g. 1024/001)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Feedback #504 — leaving this blank auto-assigns <code>{'{client}/{sequence}'}</code>: client numbers start at 1001, per-matter sequence increments per client.
              </p>
            </div>
            <div>
              <label htmlFor={fid('practice-area')} className="block text-sm font-medium text-gray-700 mb-1">Practice Area</label>
              {/* Feedback #510 — firm-configurable practice areas.  Options
                  are fetched from /api/practice-areas via useFirmPracticeAreas.
                  Includes IP subtypes (Patent, Trademark, Copyright) + 15+
                  other common areas.  Admin can manage the list via Settings.
                  Existing matter values that no longer match the list are
                  still saved (input accepts arbitrary text via datalist). */}
              <input id={fid('practice-area')}
                type="text"
                list={fid('practice-areas-list')}
                value={form.practice_area || ''}
                onChange={e => setForm({...form, practice_area: e.target.value})}
                placeholder="Select or type a practice area"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <datalist id={fid('practice-areas-list')}>
                {(practiceAreas || []).map(a => (
                  <option key={a.id} value={a.name} />
                ))}
              </datalist>
            </div>
            <div>
              <label htmlFor={fid('responsible-attorney')} className="block text-sm font-medium text-gray-700 mb-1">Responsible Attorney</label>
              <select id={fid('responsible-attorney')}
                value={form.responsible_attorney}
                onChange={e => setForm({...form, responsible_attorney: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select attorney</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}{m.title ? ` — ${m.title}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={fid('status')} className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select id={fid('status')}
                value={form.status}
                onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                {/* Round 3, "Send them to Close/Reopen": the edit form never
                    closes (the API refuses it), so Closed is offered only
                    where it is the matter's current status.  The New Matter
                    form never offers it (James 2026-10-20: POST
                    /api/matters refuses 'closed'; close with Close Matter). */}
                {editingMatter && editingOriginalStatus === 'closed' && (
                  <option value="closed">Closed</option>
                )}
              </select>
              {/* James 2026-10-19 — the edit form follows the Close / Reopen
                  rules server-side (PUT /api/matters/:id); say so before Save. */}
              {editingMatter && editingOriginalStatus === 'closed' && form.status !== 'closed' && (
                <p className="text-xs text-gray-500 mt-1">Reopening a closed matter needs an administrator. Like Reopen Matter, it clears the close date, closing notes and pre-close checklist, and asks before saving.</p>
              )}
              {editingMatter && editingOriginalStatus !== 'closed' && (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {canCloseMatters && (
                    <button type="button" onClick={goToCloseMatter}
                      className="text-xs font-medium px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
                      Close Matter…
                    </button>
                  )}
                  <span className="text-xs text-gray-500">
                    {canCloseMatters
                      ? 'To close it, use Close Matter on the matter page: it records the closing notes and checklist and checks the trust balance.'
                      : 'Closing is done with Close Matter on the matter page, by an administrator or someone with Create Matters.'}
                  </span>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label htmlFor={fid('description')} className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea id={fid('description')}
                value={form.description}
                onChange={e => setForm({...form, description: e.target.value})}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* T2 Ship A polish FE (2026-08-11) — flat_fee_amount input.
                Only meaningful when billing_type='flat_fee'; shown as an
                inline field between billing_type + retainer.  Section-dirty
                flag flipped on any interaction so the submit body includes
                the field (else backend preserves current value). */}
            {form.billing_type === 'flat_fee' && (
              <div className="md:col-span-2 -mt-2">
                <label htmlFor={fid('flat-fee-amount')} className="block text-sm font-medium text-gray-700 mb-1">
                  Flat fee amount ($)
                  <span className="text-xs font-normal text-gray-500 ml-2">The contracted price under this flat-fee arrangement (Amicus File.FixedFeeAmount).</span>
                </label>
                <input id={fid('flat-fee-amount')} type="number" step="0.01" min="0"
                  value={form.flat_fee_amount || ''}
                  onChange={e => { setForm({ ...form, flat_fee_amount: e.target.value }); setFlatFeeDirty(true) }}
                  placeholder="e.g. 5000.00"
                  className="w-full md:w-1/3 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            )}

            {/* T2 Ship I rest (2026-08-11) — per-matter bill-to override.
                All-or-nothing: fill in when this matter should be billed to
                a different party than the client's default address (e.g.
                insurance carrier for defense matters, corporate parent for
                subsidiary matters).  Missing fields fall back to the client's
                own address at invoice-render time.
                CRITICAL — the onToggle handler flips billToDirty=true when
                the section opens, and every input flips it too.  If the
                section stays closed, bill_to_* fields are OMITTED from the
                PUT body and current DB values are preserved. */}
            <details className="md:col-span-2 border-t pt-4 mt-2 group"
              onToggle={e => { if (e.currentTarget.open) setBillToDirty(true) }}>
              <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2 select-none">
                <span className="group-open:hidden">📮 Bill-to override (per-matter) ▸</span>
                <span className="hidden group-open:inline">📮 Bill-to override (per-matter) ▾</span>
                <span className="ml-2 text-xs text-gray-400 font-normal">Optional — leave blank to use the client's default address.  Use when this matter is billed to an insurance carrier, a corporate parent, or a different address than the client's default.</span>
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div className="md:col-span-2">
                  <label htmlFor={fid('bill-to-company')} className="block text-xs font-medium text-gray-600 mb-1">Company / attention line</label>
                  <input id={fid('bill-to-company')} type="text"
                    value={form.bill_to_company || ''}
                    onChange={e => { setForm({...form, bill_to_company: e.target.value}); setBillToDirty(true) }}
                    placeholder="e.g. Allstate Insurance Co, Claim #12345"
                    maxLength={255}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor={fid('bill-to-street')} className="block text-xs font-medium text-gray-600 mb-1">Street address</label>
                  <input id={fid('bill-to-street')} type="text"
                    value={form.bill_to_street || ''}
                    onChange={e => { setForm({...form, bill_to_street: e.target.value}); setBillToDirty(true) }}
                    placeholder="e.g. 1000 Insurance Way, Suite 500"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label htmlFor={fid('bill-to-city')} className="block text-xs font-medium text-gray-600 mb-1">City</label>
                  <input id={fid('bill-to-city')} type="text"
                    value={form.bill_to_city || ''}
                    onChange={e => { setForm({...form, bill_to_city: e.target.value}); setBillToDirty(true) }}
                    maxLength={100}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={fid('bill-to-state')} className="block text-xs font-medium text-gray-600 mb-1">State</label>
                    <input id={fid('bill-to-state')} type="text"
                      value={form.bill_to_state || ''}
                      onChange={e => { setForm({...form, bill_to_state: e.target.value}); setBillToDirty(true) }}
                      maxLength={50}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label htmlFor={fid('bill-to-zip')} className="block text-xs font-medium text-gray-600 mb-1">Zip</label>
                    <input id={fid('bill-to-zip')} type="text"
                      value={form.bill_to_zip || ''}
                      onChange={e => { setForm({...form, bill_to_zip: e.target.value}); setBillToDirty(true) }}
                      maxLength={20}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                </div>
              </div>
            </details>

            {/* Billing depth #4 UI — LEDES 1998B billing identifiers.  Only
                filled in when the client requires the LEDES export format
                (typical for insurance defense + corporate in-house work).
                All optional; empty falls back to firm defaults on export. */}
            <details className="md:col-span-2 border-t pt-4 mt-2 group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2 select-none">
                <span className="group-open:hidden">📋 LEDES billing identifiers ▸</span>
                <span className="hidden group-open:inline">📋 LEDES billing identifiers ▾</span>
                <span className="ml-2 text-xs text-gray-400 font-normal">Corporate/insurance-defense clients only — fill in when the client requires LEDES 1998B invoice exports.</span>
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <label htmlFor={fid('ledes-client-id')} className="block text-xs font-medium text-gray-600 mb-1">CLIENT_ID</label>
                  <input id={fid('ledes-client-id')} type="text"
                    value={form.ledes_client_id || ''}
                    onChange={e => setForm({...form, ledes_client_id: e.target.value})}
                    placeholder="Client identifier assigned by the payer"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label htmlFor={fid('ledes-law-firm-matter-id')} className="block text-xs font-medium text-gray-600 mb-1">LAW_FIRM_MATTER_ID</label>
                  <input id={fid('ledes-law-firm-matter-id')} type="text"
                    value={form.ledes_law_firm_matter_id || ''}
                    onChange={e => setForm({...form, ledes_law_firm_matter_id: e.target.value})}
                    placeholder="Falls back to File Number if blank"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label htmlFor={fid('ledes-client-matter-id')} className="block text-xs font-medium text-gray-600 mb-1">CLIENT_MATTER_ID</label>
                  <input id={fid('ledes-client-matter-id')} type="text"
                    value={form.ledes_client_matter_id || ''}
                    onChange={e => setForm({...form, ledes_client_matter_id: e.target.value})}
                    placeholder="Matter ID from the client's system"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
            </details>

            {/* Ship 7g — per-matter invoice template override.  '(Firm default)'
                surfaces which template inheriting resolves to.  Touched-flag
                on any change so absent-flag preserves the current DB value
                across unrelated saves. */}
            <details className="md:col-span-2 border-t pt-4 mt-2 group"
              onToggle={e => { if (e.currentTarget.open) setInvoiceTemplateDirty(true) }}>
              <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2 select-none">
                <span className="group-open:hidden">📄 Invoice template (this matter) ▸</span>
                <span className="hidden group-open:inline">📄 Invoice template (this matter) ▾</span>
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  {form.invoice_template_override
                    ? `Override: ${form.invoice_template_override}`
                    : `Inheriting firm default (${firmDefaultTemplate})`}
                </span>
              </summary>
              <div className="mt-3">
                <label htmlFor={fid('invoice-template-override')} className="block text-xs font-medium text-gray-600 mb-1">
                  Template for invoices generated on this matter
                </label>
                <select
                  id={fid('invoice-template-override')}
                  value={form.invoice_template_override || ''}
                  onChange={e => { setForm({...form, invoice_template_override: e.target.value}); setInvoiceTemplateDirty(true) }}
                  className="w-full md:w-2/3 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Firm default ({firmDefaultTemplate})</option>
                  <option value="standard">Standard — itemized, no Discount line</option>
                  <option value="standard_fixed_fee">Standard — Fixed Fee — hides hourly detail</option>
                  <option value="standard_new">Standard — Modern — Standard plus a Discount line</option>
                  <option value="simple_elegant">Simple Elegant — executive summary</option>
                  <option value="contingency">Contingency — % of recovery, no hours/rate columns</option>
                </select>
                <div className="mt-2 text-xs text-gray-500 space-y-1">
                  <p>
                    Changing this affects <strong>new invoices going forward only</strong>.
                    Historical invoices keep the template they were generated with.
                  </p>
                  <p>
                    Contingency template requires the matter's <code>contingency_pct</code> to be set (in the Contingency section above).
                    Fixed-Fee template pairs with the Flat fee amount field.
                  </p>
                </div>
              </div>
            </details>

            {/* Visibility — controls who can see the matter and all its child data */}
            <div className="md:col-span-2 border-t pt-4 mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">🔒 Visibility</label>
              <div className="flex gap-4 mb-3">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="vis" checked={form.visibility === 'everyone'}
                    onChange={() => setForm(f => ({ ...f, visibility: 'everyone', visible_to_users: [], visible_to_groups: [] }))} />
                  Everyone with Matters access
                </label>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="vis" checked={form.visibility === 'specific'}
                    onChange={() => setForm(f => ({ ...f, visibility: 'specific' }))} />
                  Specific users and groups
                </label>
              </div>
              <p className="text-xs text-gray-500 mb-3">The responsible attorney always has access regardless of this list. Admins always see all matters.</p>

              {form.visibility === 'specific' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase font-medium text-gray-500">Users</label>
                    <div className="mt-1 border border-gray-300 rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                      {teamMembers.length === 0 ? (
                        <p className="text-xs text-gray-400 px-2 py-1">No team members loaded.</p>
                      ) : teamMembers.map(m => (
                        <label key={m.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm">
                          <input type="checkbox" checked={form.visible_to_users.includes(m.id)} onChange={() => toggleVisUser(m.id)} />
                          <span>{m.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{form.visible_to_users.length} selected</p>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-medium text-gray-500">Groups</label>
                    <div className="mt-1 border border-gray-300 rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                      {groups.length === 0 ? (
                        <p className="text-xs text-gray-400 px-2 py-1">No groups defined yet. Admins can create groups on the Team page.</p>
                      ) : groups.map(g => (
                        <label key={g.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm">
                          <input type="checkbox" checked={form.visible_to_groups.includes(g.id)} onChange={() => toggleVisGroup(g.id)} />
                          <span>{g.name}</span>
                          <span className="text-xs text-gray-400">({g.member_count} {g.member_count === 1 ? 'member' : 'members'})</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{form.visible_to_groups.length} selected</p>
                  </div>
                </div>
              )}
            </div>

            {/* Per-matter Permission Overrides (admin only; only when editing) */}
            {editingMatter && (user?.role === 'admin' || user?.permissions?.is_admin) && (
              <PermissionOverridesSection
                matterId={editingMatter}
                teamMembers={teamMembers}
                groups={groups}
              />
            )}

            {/* Per-matter user blocks (admin only; only when editing).  A block
                is a HARD deny — stronger than a permission override.  Backend
                trigger refuses self-target and responsible-attorney target. */}
            {editingMatter && (user?.role === 'admin' || user?.permissions?.is_admin) && (
              <MatterBlockListSection
                matterId={editingMatter}
                teamMembers={teamMembers}
                currentUserId={user?.id}
                responsibleAttorneyId={form.responsible_attorney}
              />
            )}

            {/* Ship 3 — firm-configured custom fields (edit form only).  Server
                filters admin_only sets for non-admin readers.  Save button is
                hidden when canWrite=false.  Renders null when no defs exist
                for the matter scope. */}
            {editingMatter && (
              <CustomFieldsSection
                entityType="matter"
                entityId={editingMatter}
                canWrite={canEditMatters}
              />
            )}

            {/* The page-top banner is off-screen from here on a long form, so a
                refused save (trust guard, reopen without admin, bad status)
                repeats the server's message next to the button. */}
            {error && (
              <div role="alert" className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
            )}
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                {editingMatter ? 'Save Changes' : 'Save Matter'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters + Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Show:</label>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {[
                { id: 'active', label: 'Active' },
                { id: 'all', label: 'All' },
                { id: 'closed', label: 'Closed' }
              ].map(f => (
                <button key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    filter === f.id
                      ? 'bg-white text-gray-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <input type="text" placeholder="Search matters by title, client, or attorney..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>
      </div>

      {/* Bulk action bar — sticky above the table when rows selected */}
      {selectedMatterIds.size > 0 && (
        <div className="sticky top-2 z-30 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 mb-3 flex items-center gap-3 shadow-sm">
          <span className="text-sm font-medium text-primary-800">
            {selectedMatterIds.size} selected
          </span>
          <span className="text-xs text-gray-500 border-l border-primary-200 pl-3">Change status to:</span>
          {['active', 'pending', 'closed'].map(s => (
            <button key={s}
              onClick={() => handleBulkStatus(s)}
              disabled={bulkBusy}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                s === 'closed'
                  ? 'bg-gray-800 text-white hover:bg-gray-900'
                  : s === 'pending'
                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button onClick={clearMatterSelection}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700">Clear selection</button>
        </div>
      )}

      {/* Matters List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {matters.length === 0 ? (
          <EmptyState
            icon="📁"
            title="No matters yet"
            description="Matters are the individual cases you work on for clients.  Add your first matter to start tracking time, generating invoices, and managing case data."
            action={(user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_create_matters) ? { label: '+ New Matter', onClick: () => setShowForm(true) } : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox"
                    checked={visibleMatters.length > 0 && visibleMatters.every(m => selectedMatterIds.has(m.id))}
                    onChange={(e) => toggleAllVisible(visibleMatters, e.target.checked)}
                    aria-label="Select all visible matters"
                    className="rounded border-gray-300" />
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('title')}>Matter <SortIcon dir={sort.key === 'title' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('client')}>Client <SortIcon dir={sort.key === 'client' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('attorney')}>Responsible Attorney <SortIcon dir={sort.key === 'attorney' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('wip')}>Billing <SortIcon dir={sort.key === 'wip' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('status')}>Status <SortIcon dir={sort.key === 'status' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(() => {
                const filtered = visibleMatters
                if (filtered.length === 0) {
                  return (
                    <tr><td colSpan="7" className="p-8 text-center text-gray-400">
                      {search
                        ? <>No matters match <strong>"{search}"</strong>. Try a different search or change the status filter.</>
                        : filter === 'closed'
                          ? 'No closed matters yet. Active matters appear here once they\'re closed.'
                          : filter === 'active'
                            ? 'No active matters. Switch the filter to "All" to see closed matters, or click + New Matter to add one.'
                            : 'No matters match this filter.'}
                    </td></tr>
                  )
                }
                return filtered.map(matter => (
                <tr key={matter.id}
                  data-matter-id={matter.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => handleMatterClick(e, matter)}
                  onContextMenu={(e) => {
                    // Surface B: right-click a matter row → small menu with
                    // Open / Open quick-view / Open in new window.
                    //
                    // ALWAYS intercept + render the menu — earlier versions had
                    // an `if (!window.electronAPI?.openPopupWindow) return` early
                    // guard that misfired in some Electron runtime conditions,
                    // leaving the user with neither a custom menu nor a native
                    // one.  Same fix pattern as the sidebar right-click.
                    e.preventDefault()
                    const items = [
                      { label: 'Open',                icon: '↗', onClick: () => navigate(`/matters/${matter.id}`) },
                      { label: 'Open quick view',     icon: '👁', onClick: () => { setQuickView(matter); setQuickViewData(null); api.get(`/api/matters/${matter.id}`).then(res => setQuickViewData(res.data)).catch(() => {}) } },
                    ]
                    if (window.electronAPI?.openPopupWindow) {
                      items.push({ label: 'Open in new window', icon: '⤴', onClick: () => {
                        safeOpenPopupWindow(
                          `page-/matters/${matter.id}`,
                          `/popup/page/matters/${matter.id}`,
                          { width: 1300, height: 900 }
                        )
                      }})
                    }
                    setRowCtxMenu({ x: e.clientX, y: e.clientY, items })
                  }}
                  title={window.electronAPI?.openPopupWindow ? 'Click to view · Right-click for options' : ''}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleMatterClick(e, matter) }}>
                  <td className="px-3 py-4 w-8" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox"
                      checked={selectedMatterIds.has(matter.id)}
                      onChange={() => toggleMatter(matter.id)}
                      aria-label={`Select ${matter.title || 'matter'}`}
                      className="rounded border-gray-300" />
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-primary-600 hover:underline">
                      {matter.title}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {displayClientForMatterRow(matter)}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {matter.responsible_attorney_name || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-4 text-gray-600 capitalize">
                    {(matter.billing_type || '').replace('_', ' ')}
                    {matter.rate && ` • $${matter.rate}/hr`}
                  </td>
                  <td className="px-6 py-4">
                    <InlineSelect
                      value={matter.status}
                      // PUT /:id refuses a change INTO closed (use Close
                      // Matter), so an open matter's badge offers no Closed;
                      // a closed one keeps it (its current value).
                      options={matter.status === 'closed' ? STATUS_OPTIONS : STATUS_OPTIONS.filter(o => o.value !== 'closed')}
                      canEdit={canEditMatters}
                      onSave={(v) => saveInlineField(matter, 'status', v)}
                      renderValue={(v) => (
                        <span
                          title={
                            v === 'active' ? 'Active — matter is in flight; time entries, invoices, and events can be added' :
                            v === 'pending' ? 'Pending — matter is provisional (e.g. awaiting engagement letter); can be worked but not the default filter' :
                            v === 'closed' ? 'Closed — matter is finalized; no new time entries can be posted (see Close checklist)' :
                            'Matter status'
                          }
                          className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${statusColor(v)}`}>
                          {v}
                        </span>
                      )}
                    />
                  </td>
                  <td className="px-6 py-4">
                    {((user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_edit_matters) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(matter) }}
                        className="text-blue-500 hover:text-blue-700 text-sm mr-3"
                      >
                        Edit
                      </button>
                    )}
                    {((user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_delete_matters) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(matter.id) }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
              })()}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {/* Matter Quick-View Popup — body shared with /popup/matter/:id so the
          ⤴ pop-out button (Electron-only) opens the same content in its own
          OS window for floating onto a 2nd monitor. */}
      {quickView && (
        <DraggablePopup
          title={`Matter — ${quickView.title}`}
          onClose={() => setQuickView(null)}
          width={540}
          popupKey={`matter-${quickView.id}`}
          popupRoute={`/popup/matter/${quickView.id}`}
        >
          <MatterQuickViewContent
            matterId={quickView.id}
            summary={quickView}
            onClose={() => setQuickView(null)}
          />
        </DraggablePopup>
      )}

      {/* Row right-click menu (Open / Open quick view / Open in new window) */}
      <ContextMenu menu={rowCtxMenu} onClose={() => setRowCtxMenu(null)} />
    </div>
  )
}

// Per-matter Permission Overrides — admin-only section on the matter edit form.
// Each row = a REVOCATION of a specific permission for a specific user OR group
// on this matter.  Admins + the matter's responsible_attorney are exempt
// (enforced server-side regardless of row presence).
// Ship 7b F2.2 drift-audit note (2026-08-18): all values except
// `case_analysis_evaluations` map 1:1 to KNOWN_PERMS keys in
// api/src/middleware/permissions.js.  `case_analysis_evaluations` is
// deliberately NOT in KNOWN_PERMS — it's a MATTER-SCOPED-ONLY permission
// (Phase 4 #5 "Scribe role") that has no firm-wide analog.  The
// matter_permission_overrides INSERT path in matters.js accepts any string
// without running validateKnownPerms, so this override records + enforces
// correctly.  Do NOT add case_analysis_evaluations to KNOWN_PERMS — that
// would make it grantable via Team.js / Roles.js UNION which is not the
// intended semantic (Scribe is a per-matter revocation, not a firm perm).
const SCOPABLE_PERMS = [
  { value: 'calendar',       label: 'Calendar (events on this matter)' },
  { value: 'tasks',          label: 'To-Do' },
  { value: 'billing_tab',    label: 'Billing (WIP, AR, costs view)' },
  { value: 'invoices',       label: 'Invoices' },
  { value: 'time_entries',   label: 'Time Entries' },
  { value: 'communications', label: 'Communications Log' },
  { value: 'sol_tracker',    label: 'SOL Tracker' },
  { value: 'trust',          label: 'Trust (deposits / disbursements)' },
  { value: 'discovery',      label: 'Discovery (write access)' },
  // Phase 4 #5 — "Scribe role": user can edit facts/issues/questions on this
  // matter but can NOT change firm-wide custom evaluation values for any fact.
  { value: 'case_analysis_evaluations', label: 'Case Analysis Evaluations (Scribe role: revoke to block eval edits)' }
]

function PermissionOverridesSection({ matterId, teamMembers, groups }) {
  const ask = useAsk()
  const [overrides, setOverrides] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [target, setTarget] = useState({ type: 'user', id: '' })
  const [perm, setPerm] = useState('calendar')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const r = await listMatterPermissionOverrides(matterId)
      setOverrides(r.data.overrides || [])
    } catch (err) {
      // Pre-API-restart graceful degrade
      if (err.response?.status === 404 || err.response?.status === 405) setOverrides([])
      else setError(err.response?.data?.error || 'Could not load overrides')
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [matterId])

  const handleAdd = async (e) => {
    e.preventDefault()
    setError('')
    if (!target.id) return setError('Pick a user or group')
    try {
      await addMatterPermissionOverride(matterId, {
        [target.type === 'user' ? 'user_id' : 'group_id']: parseInt(target.id),
        permission: perm,
        notes: notes.trim() || null
      })
      setAdding(false); setTarget({ type: 'user', id: '' }); setPerm('calendar'); setNotes('')
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add')
    }
  }

  const handleRemove = async (id) => {
    const ok = await ask({
      title: 'Remove revocation?',
      message: 'Remove this revocation? The user/group will regain that permission on this matter.',
      confirmLabel: 'Remove revocation',
      danger: true,
    })
    if (!ok) return
    try { await removeMatterPermissionOverride(matterId, id); await load() }
    catch (err) { setError(err.response?.data?.error || 'Could not remove') }
  }

  return (
    <div className="md:col-span-2 border-2 border-amber-200 bg-amber-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-amber-900">🔐 Permission Overrides ({overrides.length})</h3>
          <p className="text-xs text-amber-800 mt-1">
            REVOKE specific permissions for specific users / groups on this matter.
            Admins and the matter's responsible attorney are exempt regardless.
          </p>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded">
            + Add Override
          </button>
        )}
      </div>

      {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 mb-2">{error}</div>}

      {adding && (
        <div className="bg-white p-3 rounded mb-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-700 mb-1">Target Type</label>
              <select value={target.type} onChange={e => setTarget({ type: e.target.value, id: '' })}
                className="w-full border rounded px-2 py-1 text-sm">
                <option value="user">User</option>
                <option value="group">Group</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">{target.type === 'user' ? 'User' : 'Group'}</label>
              <select value={target.id} onChange={e => setTarget({ ...target, id: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm">
                <option value="">— Pick one —</option>
                {target.type === 'user'
                  ? teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)
                  : groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">Permission to Revoke</label>
              <select value={perm} onChange={e => setPerm(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm">
                {SCOPABLE_PERMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">Notes (optional)</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Reason for the revocation"
                className="w-full border rounded px-2 py-1 text-sm"/>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setError('') }}
              className="text-xs px-3 py-1 text-gray-600">Cancel</button>
            <button type="button" onClick={handleAdd}
              className="text-xs px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded">
              Revoke
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-xs text-gray-500 italic">Loading…</p>}
      {!loading && overrides.length === 0 && !adding && (
        <p className="text-xs text-amber-700 italic">No overrides — everyone with visibility uses their global permissions on this matter.</p>
      )}
      {overrides.length > 0 && (
        <div className="space-y-1">
          {overrides.map(o => (
            <div key={o.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded text-xs">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-800">
                  {o.user_id
                    ? <>👤 {o.user_name || `(user #${o.user_id})`}{o.user_email && <span className="text-gray-500 font-normal"> · {o.user_email}</span>}</>
                    : <>👥 {o.group_name || `(group #${o.group_id})`}</>}
                </div>
                <div className="text-gray-600 mt-0.5">
                  Revoked: <span className="font-mono">{SCOPABLE_PERMS.find(p => p.value === o.permission)?.label || o.permission}</span>
                </div>
                {o.notes && <div className="text-gray-500 italic mt-0.5">{o.notes}</div>}
              </div>
              <button type="button" onClick={() => handleRemove(o.id)}
                className="text-red-600 hover:text-red-800">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
